import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { 
  Target, 
  Clock, 
  Mail, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Trophy,
  Users,
  Zap,
  ChevronDown,
  ChevronUp,
  Star,
  Flame
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface UserProgress {
  verified_referrals: number;
  pending_referrals: number;
  rejected_referrals: number;
  current_rank: number;
  distance_to_leader: number;
  distance_to_goal: number;
  today_referrals: number;
  velocity: string;
  estimated_rank_change: string;
  percentile: number;
}

interface Referral {
  email: string;
  status: 'verified' | 'pending' | 'rejected';
  referred_date: string;
  verified_date?: string;
  expires_at?: string;
}

interface ProgressTrackerProps {
  challengeId: string;
  challengeName: string;
  targetValue: number;
  progress: UserProgress;
  referrals: Referral[];
  onSubmitReferral: (email: string) => Promise<void>;
  loading?: boolean;
}

export function ProgressTracker({
  challengeId,
  challengeName,
  targetValue,
  progress,
  referrals,
  onSubmitReferral,
  loading = false
}: ProgressTrackerProps) {
  const [isReferralsExpanded, setIsReferralsExpanded] = useState(true);

  const progressPercentage = Math.min((progress.verified_referrals / targetValue) * 100, 100);
  
  // Log for debugging in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[PROGRESS TRACKER] Displaying ${progress.verified_referrals}/${targetValue} referrals (${progressPercentage.toFixed(1)}%)`);
  }

  // Calculate milestone markers
  const milestones = [25, 50, 75].map(percent => ({
    percent,
    position: percent,
    reached: progressPercentage >= percent,
    label: `${percent}%`
  }));

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'rejected': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800 border-green-300';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Mobile-Optimized Hero Section - Clean progress bar with key metric */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 relative overflow-hidden">
        <CardContent className="p-4 sm:p-6">
          <div className="text-center space-y-3 sm:space-y-4">
            {/* Key Metric with Animation */}
            <div className="relative">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 transition-all duration-500">
                {progress.verified_referrals}/{targetValue}
              </div>
              <p className="text-gray-600 text-xs sm:text-sm">referrals completed</p>
              {/* Debug info in development */}
              {process.env.NODE_ENV === 'development' && (
                <p className="text-xs text-gray-400 mt-1">
                  Total: {progress.verified_referrals} (includes Betfred bonus if applicable)
                </p>
              )}
              <div className="flex items-center justify-center gap-1 sm:gap-2 mt-2 flex-wrap">
                <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs sm:text-sm">
                  Rank #{progress.current_rank}
                </Badge>
                {progress.today_referrals > 0 && (
                  <Badge className="bg-green-100 text-green-800 border-green-300 animate-pulse text-xs sm:text-sm">
                    +{progress.today_referrals} today
                  </Badge>
                )}
              </div>
            </div>

            {/* Mobile-Optimized Animated Progress Bar with Milestones */}
            <div className="space-y-3">
              <div className="relative">
                <Progress 
                  value={progressPercentage} 
                  className="h-4 sm:h-6 bg-gray-200 transition-all duration-1000"
                />

                {/* Mobile-optimized milestone markers */}
                {milestones.map((milestone) => (
                  <div
                    key={milestone.percent}
                    className="absolute top-0 transform -translate-x-1/2"
                    style={{ left: `${milestone.position}%` }}
                  >
                    <div className={`
                      w-2 h-4 sm:w-3 sm:h-6 rounded-full border-2 transition-all duration-500
                      ${milestone.reached 
                        ? 'bg-yellow-400 border-yellow-500 animate-pulse' 
                        : 'bg-gray-300 border-gray-400'
                      }
                    `} />
                    <div className="absolute top-6 sm:top-8 left-1/2 transform -translate-x-1/2">
                      <span className={`text-xs font-medium ${
                        milestone.reached ? 'text-yellow-600' : 'text-gray-500'
                      }`}>
                        {milestone.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between text-xs sm:text-sm text-gray-600 mt-6 sm:mt-8">
                <span className="flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  <span className="truncate">{progressPercentage.toFixed(1)}% complete</span>
                </span>
                <span className="flex items-center gap-1">
                  <Target className="h-3 w-3" />
                  <span className="truncate">{progress.distance_to_goal} to go</span>
                </span>
              </div>
            </div>

            {/* Mobile-Optimized Consolidated Stats - Single Row with animations */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-3 sm:mt-4">
              <div className="text-center p-2 sm:p-3 bg-white rounded-lg border transition-all duration-300 hover:shadow-md">
                <div className="text-lg sm:text-xl font-bold text-green-600 transition-all duration-500">
                  {progress.verified_referrals}
                </div>
                <div className="text-xs text-gray-600">Verified</div>
              </div>
              <div className="text-center p-2 sm:p-3 bg-white rounded-lg border transition-all duration-300 hover:shadow-md">
                <div className="text-lg sm:text-xl font-bold text-yellow-600 transition-all duration-500">
                  {progress.pending_referrals}
                </div>
                <div className="text-xs text-gray-600">Pending</div>
              </div>
              <div className="text-center p-2 sm:p-3 bg-white rounded-lg border transition-all duration-300 hover:shadow-md">
                <div className="text-lg sm:text-xl font-bold text-blue-600 transition-all duration-500 flex items-center justify-center gap-1">
                  {progress.today_referrals}
                  {progress.today_referrals >= 3 && <Flame className="h-3 w-3 text-orange-500" />}
                </div>
                <div className="text-xs text-gray-600">Today</div>
              </div>
            </div>

            {/* Mobile-Optimized Velocity and Momentum Indicators */}
            {progress.velocity && parseFloat(progress.velocity.replace('/hr', '')) > 0 && (
              <div className="bg-white/50 rounded-lg p-2 sm:p-3 border border-blue-200">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <Zap className="h-3 w-3 sm:h-4 sm:w-4 text-purple-500" />
                    <span className="text-gray-600">Current velocity:</span>
                    <span className="font-bold text-purple-600">{progress.velocity}</span>
                  </div>
                  {parseFloat(progress.velocity.replace('/hr', '')) > 2 && (
                    <Badge className="bg-orange-100 text-orange-800 border-orange-300 text-xs animate-pulse">
                      🔥 Hot Streak!
                    </Badge>
                  )}
                </div>
              </div>
            )}

          </div>
        </CardContent>
      </Card>

      {/* Expandable Referrals List */}
      {referrals.length > 0 && (
        <Card className="border-2 border-gray-200">
          <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setIsReferralsExpanded(!isReferralsExpanded)}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                My Referrals ({referrals.length})
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="p-0 h-8 w-8"
                data-testid="button-toggle-referrals"
              >
                {isReferralsExpanded ? (
                  <ChevronUp className="h-5 w-5" />
                ) : (
                  <ChevronDown className="h-5 w-5" />
                )}
              </Button>
            </div>
          </CardHeader>

          {isReferralsExpanded && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                {referrals.map((referral, index) => (
                  <div
                    key={`${referral.email}-${index}`}
                    className={`
                      p-3 sm:p-4 rounded-lg border-2 transition-all duration-300
                      ${getStatusColor(referral.status)}
                    `}
                    data-testid={`referral-item-${index}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      {/* Email and Status */}
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {getStatusIcon(referral.status)}
                        <span className="font-medium text-sm sm:text-base truncate" data-testid={`referral-email-${index}`}>
                          {referral.email}
                        </span>
                      </div>

                      {/* Status Badge */}
                      <Badge 
                        className={`${getStatusColor(referral.status)} capitalize text-xs sm:text-sm`}
                        data-testid={`referral-status-${index}`}
                      >
                        {referral.status}
                      </Badge>
                    </div>

                    {/* Date Information */}
                    <div className="mt-2 space-y-1 text-xs sm:text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="h-3 w-3" />
                        <span>Referred: {new Date(referral.referred_date).toLocaleDateString()}</span>
                      </div>
                      
                      {referral.status === 'verified' && referral.verified_date && (
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="h-3 w-3" />
                          <span>Verified: {new Date(referral.verified_date).toLocaleDateString()}</span>
                        </div>
                      )}

                      {referral.status === 'pending' && referral.expires_at && (
                        <div className="flex items-center gap-2 text-yellow-700">
                          <Clock className="h-3 w-3" />
                          <span>Expires: {new Date(referral.expires_at).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary Stats */}
              <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-2 sm:gap-4 text-center">
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-green-600">
                    {referrals.filter(r => r.status === 'verified').length}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Verified</div>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-yellow-600">
                    {referrals.filter(r => r.status === 'pending').length}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Pending</div>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-red-600">
                    {referrals.filter(r => r.status === 'rejected').length}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Rejected</div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      )}

    </div>
  );
}